/**
 * <share-button-component> - the "Share" button of the product page (blocks/share.liquid).
 *
 * Touch devices open the phone's own share sheet (navigator.share); everywhere else a small menu opens with
 * WhatsApp, Facebook, X, Pinterest, Telegram, Email and Copy link. The shared link is the page the shopper is on
 * (so the chosen variant is included), without any #hash.
 */
class ShareButtonComponent extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-share-trigger]');
    this.menu = this.querySelector('[data-share-menu]');
    this.status = this.querySelector('[data-share-status]');

    this.trigger?.addEventListener('click', this.#onTrigger);
    this.menu?.addEventListener('click', this.#onMenuClick);
    document.addEventListener('click', this.#onOutside);
    document.addEventListener('keydown', this.#onKey);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.#onTrigger);
    this.menu?.removeEventListener('click', this.#onMenuClick);
    document.removeEventListener('click', this.#onOutside);
    document.removeEventListener('keydown', this.#onKey);
  }

  get shareUrl() {
    const url = new URL(window.location.href);
    url.hash = '';
    return url.toString() || this.dataset.url || '';
  }

  get shareTitle() {
    return this.dataset.title || document.title;
  }

  #onTrigger = async () => {
    const wantsNative =
      this.dataset.nativeOnTouch !== 'false' && 'share' in navigator && window.matchMedia('(pointer: coarse)').matches;

    if (wantsNative) {
      try {
        await navigator.share({ title: this.shareTitle, url: this.shareUrl });
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return; // the shopper closed the sheet
        // Anything else: fall through to the menu.
      }
    }
    this.#toggle();
  };

  #toggle(force) {
    if (!this.menu || !this.trigger) return;
    const open = force ?? this.menu.hidden;
    if (open) this.#fillLinks();
    this.menu.hidden = !open;
    this.trigger.setAttribute('aria-expanded', String(open));
    if (open) this.menu.querySelector('a, button')?.focus({ preventScroll: true });
  }

  #fillLinks() {
    const url = encodeURIComponent(this.shareUrl);
    const title = encodeURIComponent(this.shareTitle);
    const image = encodeURIComponent(this.dataset.image || '');
    const hrefs = {
      whatsapp: `https://wa.me/?text=${title}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      x: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${url}&media=${image}&description=${title}`,
      telegram: `https://t.me/share/url?url=${url}&text=${title}`,
      email: `mailto:?subject=${title}&body=${title}%0A${url}`,
    };
    for (const link of this.querySelectorAll('[data-share-link]')) {
      const href = hrefs[link.getAttribute('data-share-link') || ''];
      if (href) link.setAttribute('href', href);
    }
  }

  /** @param {MouseEvent} event */
  #onMenuClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-share-copy]')) {
      const copied = await this.#copy(this.shareUrl);
      const button = target.closest('[data-share-copy]');
      if (button) {
        const original = button.textContent;
        button.textContent = copied ? 'Link copied!' : 'Press Ctrl+C to copy';
        if (this.status) this.status.textContent = copied ? 'Link copied' : '';
        setTimeout(() => {
          button.textContent = original;
          this.#toggle(false);
        }, 1200);
      }
      return;
    }

    if (target.closest('[data-share-link]')) setTimeout(() => this.#toggle(false), 100);
  };

  /** @param {string} text */
  async #copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const input = document.createElement('textarea');
      input.value = text;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.append(input);
      input.select();
      let done = false;
      try {
        done = document.execCommand('copy');
      } catch {
        done = false;
      }
      input.remove();
      return done;
    }
  }

  /** @param {MouseEvent} event */
  #onOutside = (event) => {
    if (this.menu && !this.menu.hidden && event.target instanceof Node && !this.contains(event.target)) this.#toggle(false);
  };

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    if (event.key === 'Escape' && this.menu && !this.menu.hidden) {
      this.#toggle(false);
      this.trigger?.focus();
    }
  };
}

if (!customElements.get('share-button-component')) {
  customElements.define('share-button-component', ShareButtonComponent);
}
